import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import apiService from '../../services/apiService';

const DMC_ITINERARY_FORM_CACHE_KEY = 'dmc_itinerary_form_cache_v1';
const MEDIA_URL = process.env.REACT_APP_MEDIA_URL || '';

const joinLocationNames = (locations = []) => {
  if (!Array.isArray(locations) || locations.length === 0) {
    return '--';
  }

  return locations.map((location) => location?.name).filter(Boolean).join(', ') || '--';
};

const getPdfUrl = (path = '') => {
  if (!path) return '';
  if (/^https?:\/\//i.test(path)) return path;
  return `${MEDIA_URL}${path}`;
};

const writeDmcItineraryCache = (payload = {}) => {
  try {
    sessionStorage.setItem(DMC_ITINERARY_FORM_CACHE_KEY, JSON.stringify(payload));
  } catch (error) {
    console.error('Failed to cache DMC itinerary form data:', error);
  }
};

const DMCItinerary = () => {
  const navigate = useNavigate();
  const [itineraries, setItineraries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [keyword, setKeyword] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [deletedFilter, setDeletedFilter] = useState('not_deleted');
  const [currentPage, setCurrentPage] = useState(1);
  const [recordsPerPage, setRecordsPerPage] = useState(10);
  const [totalRecords, setTotalRecords] = useState(0);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteId, setDeleteId] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    fetchItineraries();
  }, [currentPage, recordsPerPage, keyword, statusFilter, deletedFilter]);

  useEffect(() => {
    prefetchDmcItineraryFormData();
  }, []);

  const fetchItineraries = async () => {
    try {
      setLoading(true);
      const response = await apiService.post('dmcItineraries', {
        start: (currentPage - 1) * recordsPerPage,
        length: recordsPerPage,
        search: keyword,
        statusFilter,
        deletedFilter
      });

      setItineraries(response.data || []);
      setTotalRecords(response.recordsFiltered || 0);
    } catch (error) {
      console.error('DMC itinerary fetch error:', error);
      toast.error('Failed to load DMC itinerary list');
    } finally {
      setLoading(false);
    }
  };

  const prefetchDmcItineraryFormData = async () => {
    try {
      const response = await apiService.get('addDmcItinerary');
      const payload = response?.data || {};

      if ((payload.dmcs || []).length > 0 || (payload.locations || []).length > 0) {
        writeDmcItineraryCache(payload);
      }
    } catch (error) {
      console.error('DMC itinerary prefetch error:', error);
    }
  };

  const handleStatusToggle = async (id) => {
    try {
      const response = await apiService.get(`dmcItineraryStatus/${id}`);
      if (response.status) {
        setItineraries((prev) => prev.map((item) => (
          item._id === id ? { ...item, status: item.status === 1 ? 0 : 1 } : item
        )));
        toast.success(response.message || 'Status updated successfully');
      } else {
        toast.error(response.message || 'Failed to update status');
      }
    } catch (error) {
      console.error('DMC itinerary status error:', error);
      toast.error('Something went wrong');
    }
  };

  const openDeleteModal = (id) => {
    setDeleteId(id);
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    try {
      setSubmitting(true);
      const response = await apiService.get(`deleteDmcItinerary/${deleteId}`);
      if (response.status) {
        toast.success(response.message || 'DMC itinerary deleted successfully');
        setShowDeleteModal(false);
        setDeleteId('');
        fetchItineraries();
      } else {
        toast.error(response.message || 'Failed to delete DMC itinerary');
      }
    } catch (error) {
      console.error('DMC itinerary delete error:', error);
      toast.error('Something went wrong, try again!!');
    } finally {
      setSubmitting(false);
    }
  };

  const totalPages = Math.ceil(totalRecords / recordsPerPage);
  const startRecord = totalRecords === 0 ? 0 : (currentPage - 1) * recordsPerPage + 1;
  const endRecord = Math.min(currentPage * recordsPerPage, totalRecords);

  return (
    <div className="p-0">
      <div className="mb-3">
        <nav className="flex flex-wrap items-center text-xs sm:text-sm">
          <Link to="/dashboard" className="text-gray-500 hover:text-gray-700 no-underline">Home</Link>
          <span className="mx-2 text-gray-400">/</span>
          <span className="text-gray-900 font-medium">DMC Manage</span>
          <span className="mx-2 text-gray-400">/</span>
          <span className="text-gray-900 font-medium">DMC Itinerary</span>
        </nav>
      </div>

      <div className="bg-white rounded-lg shadow-sm">
        <div className="p-4 sm:p-6 border-b flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
          <div className="flex flex-1 flex-col sm:flex-row gap-3">
            <div className="relative flex-1 min-w-[220px]">
              <i className="bi bi-search absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"></i>
              <input
                type="text"
                value={keyword}
                onChange={(e) => {
                  setKeyword(e.target.value);
                  setCurrentPage(1);
                }}
                placeholder="Search by duration, DMC, PDF link, or itinerary"
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg text-sm"
              />
            </div>
            <select
              value={statusFilter}
              onChange={(e) => {
                setStatusFilter(e.target.value);
                setCurrentPage(1);
              }}
              className="px-3 py-2 border border-gray-300 rounded-lg text-sm bg-white"
            >
              <option value="all">Status: All</option>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
            </select>
            <select
              value={deletedFilter}
              onChange={(e) => {
                setDeletedFilter(e.target.value);
                setCurrentPage(1);
              }}
              className="px-3 py-2 border border-gray-300 rounded-lg text-sm bg-white"
            >
              <option value="not_deleted">Deleted: No</option>
              <option value="deleted">Deleted: Yes</option>
              <option value="all">Deleted: All</option>
            </select>
          </div>

          <button
            type="button"
            onClick={() => navigate('/dmc-itinerary/create')}
            className="inline-flex items-center justify-center gap-2 px-4 py-2 text-sm font-medium text-white rounded-lg"
            style={{ backgroundColor: '#009ef7' }}
          >
            <i className="bi bi-plus-lg"></i>
            Add DMC Itinerary
          </button>
        </div>

        <div className="p-2">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2" style={{ borderColor: '#009ef7' }}></div>
              <span className="ml-2 text-gray-600">Loading...</span>
            </div>
          ) : itineraries.length === 0 ? (
            <div className="text-center py-12">
              <i className="bi bi-journal-text text-4xl text-gray-300 mb-2"></i>
              <h3 className="text-lg font-medium text-gray-900 mb-1">No DMC Itinerary Found</h3>
              <p className="text-gray-500">Create your first DMC itinerary to get started.</p>
            </div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="w-full text-xs sm:text-sm">
                  <thead className="bg-gray-50">
                    <tr className="text-left text-gray-500 text-xs uppercase">
                      <th className="px-4 py-3">#</th>
                      <th className="px-4 py-3">DMC</th>
                      <th className="px-4 py-3">Duration</th>
                      <th className="px-4 py-3">Location Cover</th>
                      <th className="px-4 py-3">PDF</th>
                      <th className="px-4 py-3">Status</th>
                      <th className="px-4 py-3">Action</th>
                      <th className="px-4 py-3">CreatedAt</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {itineraries.map((item, index) => {
                      const uploadUrl = getPdfUrl(item.pdfUpload);
                      return (
                        <tr key={item._id} className="hover:bg-gray-50">
                          <td className="px-4 py-3">{startRecord + index}</td>
                          <td className="px-4 py-3 font-medium text-gray-900">{item.dmc?.name || '--'}</td>
                          <td className="px-4 py-3">{item.duration || '--'}</td>
                          <td className="px-4 py-3">{joinLocationNames(item.locationCover)}</td>
                          <td className="px-4 py-3">
                            <div className="flex flex-col gap-1">
                              {uploadUrl ? (
                                <a
                                  href={uploadUrl}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="text-[#009ef7] hover:underline"
                                >
                                  Uploaded PDF
                                </a>
                              ) : null}
                              {item.pdfLink ? (
                                <a
                                  href={item.pdfLink}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="text-[#009ef7] hover:underline"
                                >
                                  PDF Link
                                </a>
                              ) : null}
                              {!uploadUrl && !item.pdfLink ? '--' : null}
                            </div>
                          </td>
                          <td className="px-4 py-3">
                            {item.deleted === 1 ? (
                              <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-500">
                                Deleted
                              </span>
                            ) : (
                              <label className="relative inline-flex items-center cursor-pointer">
                                <input
                                  type="checkbox"
                                  checked={item.status === 1}
                                  onChange={() => handleStatusToggle(item._id)}
                                  className="sr-only peer"
                                />
                                <div
                                  className="w-11 h-6 bg-gray-200 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all"
                                  style={item.status === 1 ? { backgroundColor: '#009ef7' } : {}}
                                ></div>
                              </label>
                            )}
                          </td>
                          <td className="px-4 py-3">
                            <div className="flex items-center gap-3">
                              <button
                                type="button"
                                onClick={() => navigate(`/dmc-itinerary/edit/${item._id}`)}
                                className="text-green-600 hover:text-green-800"
                                title="Edit"
                              >
                                <i className="bi bi-pencil text-lg"></i>
                              </button>
                              {item.deleted !== 1 && (
                                <button
                                  type="button"
                                  onClick={() => openDeleteModal(item._id)}
                                  className="text-red-600 hover:text-red-800"
                                  title="Delete"
                                >
                                  <i className="bi bi-trash text-lg"></i>
                                </button>
                              )}
                            </div>
                          </td>
                          <td className="px-4 py-3 text-gray-600">
                            {item.createdAt ? new Date(item.createdAt).toLocaleString('en-IN', { timeZone: 'Asia/Kolkata' }) : '--'}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              <div className="bg-gray-50 px-4 sm:px-6 py-4 border-t border-gray-200 flex flex-col sm:flex-row items-center justify-between gap-3 mt-4">
                <div className="flex items-center gap-2">
                  <span className="text-xs sm:text-sm text-gray-700">Show</span>
                  <select
                    value={recordsPerPage}
                    onChange={(e) => {
                      setRecordsPerPage(Number(e.target.value));
                      setCurrentPage(1);
                    }}
                    className="px-3 py-1 border border-gray-300 rounded-lg text-xs sm:text-sm bg-white text-gray-700"
                  >
                    <option value={10}>10</option>
                    <option value={25}>25</option>
                    <option value={50}>50</option>
                    <option value={100}>100</option>
                  </select>
                  <span className="text-xs sm:text-sm text-gray-700">entries</span>
                  <span className="text-xs sm:text-sm text-gray-500 hidden md:inline">({startRecord} to {endRecord} of {totalRecords})</span>
                </div>

                <div className="flex items-center gap-1 flex-wrap justify-center">
                  <button
                    onClick={() => setCurrentPage(1)}
                    disabled={currentPage === 1}
                    className="px-3 py-1 border border-gray-300 rounded text-xs sm:text-sm disabled:opacity-50 hover:bg-gray-100"
                  >
                    &laquo;
                  </button>
                  {[...Array(totalPages)].map((_, i) => {
                    const page = i + 1;
                    if (page === 1 || page === totalPages || (page >= currentPage - 1 && page <= currentPage + 1)) {
                      return (
                        <button
                          key={page}
                          onClick={() => setCurrentPage(page)}
                          className={`px-3 py-1 border rounded text-xs sm:text-sm ${currentPage === page ? 'bg-[#009ef7] text-white border-[#009ef7]' : 'border-gray-300 hover:bg-gray-100'}`}
                        >
                          {page}
                        </button>
                      );
                    }
                    if (page === currentPage - 2 || page === currentPage + 2) {
                      return <span key={page} className="px-2 text-gray-500">...</span>;
                    }
                    return null;
                  })}
                  <button
                    onClick={() => setCurrentPage(totalPages)}
                    disabled={currentPage === totalPages || totalPages === 0}
                    className="px-3 py-1 border border-gray-300 rounded text-xs sm:text-sm disabled:opacity-50 hover:bg-gray-100"
                  >
                    &raquo;
                  </button>
                </div>
              </div>
            </>
          )}
        </div>
      </div>

      {showDeleteModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold">Confirmation</h3>
              <button onClick={() => setShowDeleteModal(false)} className="text-gray-400 hover:text-gray-600">
                <i className="bi bi-x-lg"></i>
              </button>
            </div>
            <p className="text-gray-600 mb-6">Are you sure you want to delete this DMC itinerary?</p>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setShowDeleteModal(false)}
                disabled={submitting}
                className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300"
              >
                Cancel
              </button>
              <button
                onClick={confirmDelete}
                disabled={submitting}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 flex items-center gap-2"
              >
                {submitting ? (
                  <>
                    <span className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></span>
                    Please wait...
                  </>
                ) : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DMCItinerary;
